/**
 * Shared presentational primitives for the Brands & Vendors area.
 *
 * These wrap raw `Box`/`ButtonBase` rather than MUI's themed controls because
 * the handoff pins exact sizes, radii and colours that the global theme does
 * not carry. Anything interactive is a real button with an accessible name.
 */

import React from 'react';
import { Box, ButtonBase, SxProps, Theme, Tooltip } from '@mui/material';
import {
  bvCardSx,
  bvColor,
  bvRadius,
  bvShadow,
  bvTransition,
  bvType,
  tabular,
} from '../brandVendorsTokens';
import { CHIP_STATE_STYLE, ChipState, ReadinessChip, STAGE_LABEL, StageKey } from '../brandLifecycle';
import { EmptyState, SectionCard, StatusChip, StatusTone } from '../../../components/ui';
import { tokens } from '../../../theme/tokens';

/* ------------------------------------------------------------------ icons */

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const IconImage: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <rect x="3" y="3.5" width="18" height="17" rx="2.5" />
    <circle cx="8.7" cy="9.3" r="1.7" />
    <path d="M21 16.2l-5.1-5.1-6.2 6.2" />
  </svg>
);

export const IconBox: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M21 8.2l-9-5-9 5v7.6l9 5 9-5V8.2z" />
    <path d="M3 8.2l9 5 9-5M12 13.2v8" />
  </svg>
);

export const IconSync: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M4 7.6h13.2l-3.4-3.4M20 16.4H6.8l3.4 3.4" />
  </svg>
);

/** The claim glyph carries its state in the mark beside the figure. */
export const IconClaim: React.FC<{ size?: number; state?: ChipState }> = ({ size = 13, state = 'ok' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2.2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M14 20v-1.5a4 4 0 00-4-4H6a4 4 0 00-4 4V20" />
    <circle cx="8" cy="7" r="3.6" />
    {state === 'ok' && <path d="M15.5 11.5l2.4 2.4 4.1-4.6" />}
    {state === 'part' && <path d="M18.5 6.5v5.2M18.5 15.2v.1" />}
    {state === 'no' && <path d="M15.6 8.4l5.8 5.8M21.4 8.4l-5.8 5.8" />}
  </svg>
);

export const IconArrowOut: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M8 16L16 8M9.5 8H16v6.5" />
  </svg>
);

export const IconSearch: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="M15.4 15.4L20 20" />
  </svg>
);

export const IconChevron: React.FC<{ size?: number; dir?: 'up' | 'down' | 'left' | 'right' }> = ({
  size = 14,
  dir = 'down',
}) => {
  const rotate = { up: 180, down: 0, left: 90, right: -90 }[dir];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      strokeWidth={2}
      {...strokeProps}
      style={{ display: 'block', transform: `rotate(${rotate}deg)` }}
    >
      <path d="M6 9.5l6 6 6-6" />
    </svg>
  );
};

export const IconPlus: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconClose: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth={2} {...strokeProps} style={{ display: 'block' }}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/* ------------------------------------------------------------------ atoms */

export const StatusDot: React.FC<{ color: string; size?: number; sx?: SxProps<Theme> }> = ({
  color,
  size = 6,
  sx,
}) => (
  <Box
    component="span"
    aria-hidden
    sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0, ...sx }}
  />
);

export const Eyebrow: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Box component="div" sx={{ ...bvType.eyebrow, ...sx }}>
    {children}
  </Box>
);

/** A bordered white tile holding brand art, falling back to a monogram. */
export const LogoFrame: React.FC<{
  name: string;
  url?: string | null;
  size?: number;
  radius?: number;
  borderColor?: string;
  artSize?: number;
  monogramSize?: number;
  elevated?: boolean;
  sx?: SxProps<Theme>;
}> = ({
  name,
  url,
  size = 40,
  radius = bvRadius.control,
  borderColor = bvColor.borderSubtle,
  artSize,
  monogramSize,
  elevated = false,
  sx,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      border: `1px solid ${borderColor}`,
      borderRadius: `${radius}px`,
      bgcolor: bvColor.surface,
      display: 'grid',
      placeItems: 'center',
      overflow: 'hidden',
      boxShadow: elevated ? bvShadow.logoFrame : 'none',
      ...sx,
    }}
  >
    {url ? (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          maxWidth: artSize ?? size * 0.8,
          maxHeight: artSize ?? size * 0.8,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    ) : (
      <Box
        component="span"
        aria-hidden
        sx={{
          fontSize: monogramSize ?? Math.round(size * 0.35),
          fontWeight: 600,
          color: bvColor.muted2,
          lineHeight: 1,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Box>
    )}
  </Box>
);

/** Stage → shared signal tone. `ingesting` reads as informational rather than a health signal. */
const STAGE_TONE: Record<StageKey | 'requested', StatusTone> = {
  registered: 'caution',
  ingesting: 'info',
  claimed: 'positive',
  complete: 'positive',
  requested: 'caution',
};

/** Only `ingesting` gets a tinted fill in the handoff; the rest are a hairline outline on white. */
const STAGE_VARIANT: Record<StageKey | 'requested', 'soft' | 'outlined'> = {
  registered: 'outlined',
  ingesting: 'soft',
  claimed: 'outlined',
  complete: 'outlined',
  requested: 'outlined',
};

export const StagePill: React.FC<{ stage: StageKey | 'requested'; label?: string; sx?: SxProps<Theme> }> = ({
  stage,
  label,
  sx,
}) => (
  <StatusChip
    label={label ?? (stage === 'requested' ? 'Requested' : STAGE_LABEL[stage])}
    tone={STAGE_TONE[stage]}
    variant={STAGE_VARIANT[stage]}
    sx={{ borderRadius: `${bvRadius.pill}px`, ...bvType.stagePill, ...sx }}
  />
);

/* ------------------------------------------------------- readiness chips */

const CHIP_STATE_MARK: Record<ChipState, string> = { ok: '✓', part: '!', no: '×' };

const chipGlyph = (chip: ReadinessChip) => {
  switch (chip.key) {
    case 'logo':
      return <IconImage />;
    case 'products':
      return <IconBox />;
    case 'ingestion':
      return <IconSync />;
    default:
      return <IconClaim state={chip.state} />;
  }
};

/**
 * One readiness chip: a real button whose accessible name is the tooltip text,
 * so the affordance is not hover-only.
 */
export const ReadinessChipButton: React.FC<{ chip: ReadinessChip; onActivate: () => void }> = ({
  chip,
  onActivate,
}) => {
  const [open, setOpen] = React.useState(false);
  const style = CHIP_STATE_STYLE[chip.state];

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <ButtonBase
        aria-label={chip.ariaLabel}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          // The row itself is clickable; a chip click must not also open the peek.
          event.stopPropagation();
          onActivate();
        }}
        sx={{
          width: 26,
          height: 22,
          borderRadius: `${bvRadius.chip}px`,
          bgcolor: bvColor.surface,
          border: `1px solid ${style.border}`,
          color: style.ink,
          transition: bvTransition,
          '&:hover': { borderColor: bvColor.accent },
          '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
        }}
      >
        {chipGlyph(chip)}
        {/* Second, non-colour cue for the state: tick / dash / cross. */}
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -5,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: bvColor.surface,
            border: `1px solid ${style.border}`,
            color: style.ink,
            fontSize: 9,
            lineHeight: '10px',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {CHIP_STATE_MARK[chip.state]}
        </Box>
      </ButtonBase>
      {open && (
        <Box
          role="presentation"
          sx={{
            position: 'absolute',
            bottom: 'calc(100% + 7px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 206,
            zIndex: 20,
            bgcolor: bvColor.ink,
            color: bvColor.surface,
            borderRadius: `${bvRadius.control}px`,
            px: '9px',
            py: '11px',
            boxShadow: bvShadow.tooltip,
            pointerEvents: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '100%',
              left: '50%',
              marginLeft: '-5px',
              borderWidth: 5,
              borderStyle: 'solid',
              borderColor: `${bvColor.ink} transparent transparent transparent`,
            },
          }}
        >
          <Box sx={{ ...bvType.caption, fontWeight: 600, mb: '2px' }}>
            {chip.name} — <Box component="span" sx={{ color: style.tipInk }}>{chip.stateWord}</Box>
          </Box>
          <Box sx={{ ...bvType.microCaption, color: tokens.ink.onDark, lineHeight: 1.45 }}>{chip.detail}</Box>
          <Box sx={{ ...bvType.microCaption, color: bvColor.muted2, mt: '5px' }}>Click → {chip.action}</Box>
        </Box>
      )}
    </Box>
  );
};

/** Legend for the four readiness icons and the three states, so the column reads without hovering. */
export const ReadinessLegend: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: '14px',
      rowGap: '2px',
      px: '16px',
      py: '7px',
      borderBottom: `1px solid ${bvColor.border}`,
      ...bvType.caption,
      color: bvColor.muted,
    }}
  >
    <Box component="span" sx={{ fontWeight: 600, color: bvColor.ink3 }}>Readiness icons</Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconImage size={12} /> Logo</Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconBox size={12} /> Products</Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconSync size={12} /> Ingestion</Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconClaim size={12} /> Claim</Box>
    <Box component="span">✓ ready · ! needs work · × missing (hover an icon for detail)</Box>
  </Box>
);

export const ReadinessChipRow: React.FC<{
  chips: ReadinessChip[];
  onActivate: (chip: ReadinessChip) => void;
}> = ({ chips, onActivate }) => (
  <Box sx={{ display: 'flex', gap: '5px' }}>
    {chips.map((chip) => (
      <ReadinessChipButton key={chip.key} chip={chip} onActivate={() => onActivate(chip)} />
    ))}
  </Box>
);

/* ---------------------------------------------------------------- buttons */

type ButtonTone = 'primary' | 'default' | 'danger' | 'warning';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

type TypeStyle = { fontSize: number; fontWeight: number };

const BUTTON_SIZE: Record<ButtonSize, { height: number; px: string; radius: number; font: TypeStyle }> = {
  xs: { height: 26, px: '9px', radius: bvRadius.xsmall, font: bvType.buttonSmall },
  sm: { height: 28, px: '10px', radius: bvRadius.xsmall, font: bvType.buttonSmall },
  md: { height: 32, px: '12px', radius: bvRadius.small, font: bvType.button },
  lg: { height: 36, px: '14px', radius: bvRadius.control, font: bvType.button },
};

export const BvButton: React.FC<{
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  tone?: ButtonTone;
  size?: ButtonSize;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  type?: 'button' | 'submit';
  sx?: SxProps<Theme>;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  /** Shown as a tooltip while the button is disabled, so a greyed-out action explains itself. */
  disabledReason?: string;
}> = ({
  children,
  onClick,
  tone = 'default',
  size = 'md',
  disabled = false,
  startIcon,
  endIcon,
  type = 'button',
  sx,
  disabledReason,
  ...rest
}) => {
  const dims = BUTTON_SIZE[size];
  // Plain objects rather than SxProps so they can be spread into the sx literal below.
  const tones: Record<ButtonTone, Record<string, unknown>> = {
    primary: {
      bgcolor: bvColor.accent,
      color: bvColor.surface,
      border: `1px solid ${bvColor.accent}`,
      '&:hover': { bgcolor: bvColor.accentHover, borderColor: bvColor.accentHover },
    },
    default: {
      bgcolor: bvColor.surface,
      color: bvColor.ink3,
      border: `1px solid ${bvColor.border}`,
      '&:hover': { borderColor: bvColor.accent, color: bvColor.accentHover },
    },
    danger: {
      bgcolor: bvColor.surface,
      color: bvColor.ink3,
      border: `1px solid ${bvColor.border}`,
      '&:hover': { borderColor: bvColor.dangerBorder, color: bvColor.danger },
    },
    warning: {
      bgcolor: bvColor.warningBg,
      color: bvColor.warning,
      border: `1px solid ${bvColor.warningBorder2}`,
      '&:hover': { borderColor: bvColor.warningBar },
    },
  };

  const button = (
    <ButtonBase
      type={type}
      disabled={disabled}
      onClick={onClick}
      sx={{
        height: dims.height,
        px: dims.px,
        gap: '6px',
        borderRadius: `${dims.radius}px`,
        whiteSpace: 'nowrap',
        transition: bvTransition,
        ...dims.font,
        ...tones[tone],
        // One disabled look for every tone: flat, neutral, readable. Never a washed-out accent fill.
        '&.Mui-disabled': {
          opacity: 1,
          bgcolor: bvColor.page,
          color: bvColor.muted,
          border: `1px solid ${bvColor.borderSubtle}`,
        },
        '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
        ...sx,
      }}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </ButtonBase>
  );

  // A disabled button swallows pointer events, so the tooltip hangs off a wrapper.
  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} arrow placement="top">
        <Box component="span" sx={{ display: 'inline-flex', cursor: 'not-allowed', ...(sx as object) }}>
          {button}
        </Box>
      </Tooltip>
    );
  }
  return button;
};

/* ------------------------------------------------------------------ cards */

/**
 * `BvCard` is a thin wrapper over the shared `SectionCard` (`dense`, so tables
 * and lists still run edge to edge with no body padding). Passing `title`
 * renders `SectionCard`'s own hairline-divided header — replacing the old
 * standalone `BvCardHeader` child — so callers move from
 * `<BvCard><BvCardHeader title=… /> …</BvCard>` to
 * `<BvCard title=…>…</BvCard>`. A caller that still needs the raw bordered
 * box for layout (a non-`section` `component`, e.g. for a `<form>` wrapper)
 * gets the original `Box` + `bvCardSx` markup instead.
 */
export const BvCard: React.FC<{
  children: React.ReactNode;
  title?: React.ReactNode;
  caption?: React.ReactNode;
  action?: React.ReactNode;
  leading?: React.ReactNode;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
}> = ({ children, title, caption, action, leading, sx, component }) =>
  component ? (
    <Box component={component} sx={{ ...bvCardSx, ...sx }}>
      {children}
    </Box>
  ) : (
    <SectionCard dense title={title} subtitle={caption} actions={action} leading={leading} sx={sx}>
      {children}
    </SectionCard>
  );

/**
 * @deprecated Superseded by `BvCard`'s own `title`/`caption`/`action`/`leading`
 * props, which render the same header through the shared `SectionCard`. Kept
 * only so any code still importing it keeps compiling; no page in this area
 * uses it any more.
 */
export const BvCardHeader: React.FC<{
  title: React.ReactNode;
  caption?: React.ReactNode;
  action?: React.ReactNode;
  leading?: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, caption, action, leading, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '9px',
      px: '16px',
      py: '13px',
      borderBottom: `1px solid ${bvColor.borderSubtle}`,
      ...sx,
    }}
  >
    {leading}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ ...bvType.cardTitle, color: bvColor.ink }}>{title}</Box>
      {caption && <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '2px' }}>{caption}</Box>}
    </Box>
    {action}
  </Box>
);

/** Second empty state, now a thin wrapper over the shared `EmptyState` (`size="compact"`). */
export const BvEmptyState: React.FC<{
  headline: string;
  note?: React.ReactNode;
  action?: React.ReactNode;
  /** Vertical padding; the area's roomy 48px default, not the kit's compact 24px. */
  py?: number | string;
}> = ({ headline, note, action, py = '48px' }) => (
  <EmptyState size="compact" title={headline} description={note} action={action} sx={{ py }} />
);

/** Label/value rows with a fixed 118px label column, used by the peek and case panels. */
export const DefinitionRows: React.FC<{
  rows: Array<{ label: string; value: React.ReactNode }>;
  labelWidth?: number;
  sx?: SxProps<Theme>;
}> = ({ rows, labelWidth = 118, sx }) => (
  <Box sx={sx}>
    {rows.map((row, index) => (
      <Box
        key={row.label}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: `${labelWidth}px 1fr` },
          gap: '12px',
          px: '13px',
          py: '9px',
          borderTop: index === 0 ? 'none' : `1px solid ${bvColor.borderSubtle}`,
        }}
      >
        <Box sx={{ ...bvType.caption, color: bvColor.muted }}>{row.label}</Box>
        <Box sx={{ ...bvType.tableSecondary, color: bvColor.ink3, minWidth: 0 }}>{row.value}</Box>
      </Box>
    ))}
  </Box>
);

/** A thin progress track; `tone` picks the fill colour from the health palette. */
export const ProgressBar: React.FC<{ percent: number; tone?: ChipState; height?: number; sx?: SxProps<Theme> }> = ({
  percent,
  tone = 'ok',
  height = 6,
  sx,
}) => {
  const fill =
    tone === 'ok' ? bvColor.successBar : tone === 'part' ? bvColor.warningBar : bvColor.danger;
  return (
    <Box
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={{ height, borderRadius: `${bvRadius.bar}px`, bgcolor: bvColor.borderSubtle, overflow: 'hidden', ...sx }}
    >
      <Box sx={{ width: `${Math.max(0, Math.min(100, percent))}%`, height: '100%', bgcolor: fill }} />
    </Box>
  );
};

/** The 30×17 pill switch used on the dashboard's monitoring rows. */
export const BvSwitch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  width?: number;
  height?: number;
}> = ({ checked, onChange, disabled = false, label, width = 30, height = 17 }) => {
  const knob = height - 6;
  return (
    <ButtonBase
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      sx={{
        width,
        height,
        flexShrink: 0,
        borderRadius: `${height}px`,
        bgcolor: checked ? bvColor.accent : bvColor.border,
        border: `1px solid ${checked ? bvColor.accent : bvColor.border}`,
        transition: bvTransition,
        justifyContent: 'flex-start',
        px: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 2 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: knob,
          height: knob,
          borderRadius: '50%',
          bgcolor: bvColor.surface,
          transform: `translateX(${checked ? width - knob - 6 : 0}px)`,
          transition: 'transform 150ms ease',
        }}
      />
    </ButtonBase>
  );
};

/** Segmented control on a tinted track; the selected option gets a white pill. */
export function SegmentedFilter<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<{ value: T; label: string; count?: number }>;
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        gap: '3px',
        p: '3px',
        bgcolor: bvColor.borderSubtle,
        borderRadius: `${bvRadius.control}px`,
      }}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            role="radio"
            aria-checked={on}
            onClick={() => onChange(option.value)}
            sx={{
              height: 28,
              px: '11px',
              gap: '6px',
              borderRadius: `${bvRadius.xsmall}px`,
              bgcolor: on ? bvColor.surface : 'transparent',
              boxShadow: on ? bvShadow.card : 'none',
              color: on ? bvColor.ink : bvColor.ink3,
              transition: bvTransition,
              ...bvType.buttonSmall,
              fontWeight: on ? 600 : 500,
              '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
            }}
          >
            {option.label}
            {typeof option.count === 'number' && (
              <Box component="span" sx={{ color: bvColor.muted, ...tabular }}>
                {option.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** A joined pair of 28px stepper buttons (peek drawer, dashboard brand stepper). */
export const Stepper: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  prevLabel: string;
  nextLabel: string;
  vertical?: boolean;
}> = ({ onPrev, onNext, prevDisabled, nextDisabled, prevLabel, nextLabel, vertical = false }) => {
  const base: Record<string, unknown> = {
    width: 28,
    height: 28,
    color: bvColor.ink3,
    transition: bvTransition,
    '&:hover': { bgcolor: bvColor.borderSubtle },
    '&.Mui-disabled': { color: bvColor.muted2 },
    '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: -2 },
  };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        border: `1px solid ${bvColor.border}`,
        borderRadius: `${bvRadius.small}px`,
        overflow: 'hidden',
      }}
    >
      <ButtonBase aria-label={prevLabel} disabled={prevDisabled} onClick={onPrev} sx={base}>
        <IconChevron dir={vertical ? 'up' : 'left'} size={13} />
      </ButtonBase>
      <ButtonBase
        aria-label={nextLabel}
        disabled={nextDisabled}
        onClick={onNext}
        sx={{ ...base, borderLeft: `1px solid ${bvColor.border}` }}
      >
        <IconChevron dir={vertical ? 'down' : 'right'} size={13} />
      </ButtonBase>
    </Box>
  );
};

/** Page H1 block: title, one-line description, and right-aligned actions. */
export const BvPageHeader: React.FC<{
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, description, actions }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Box component="h1" sx={{ ...bvType.h1, color: bvColor.ink, m: 0 }}>
        {title}
      </Box>
      {description && (
        <Box sx={{ ...bvType.body, color: bvColor.ink3, mt: '5px', maxWidth: 620 }}>{description}</Box>
      )}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: '9px', alignItems: 'center' }}>{actions}</Box>}
  </Box>
);

/** A bordered search input matching the directory toolbar. */
export const BvSearchInput: React.FC<{
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  width?: number;
  ariaLabel: string;
}> = ({ value, onChange, placeholder, width = 280, ariaLabel }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '7px',
      width,
      height: 32,
      px: '10px',
      bgcolor: bvColor.surface,
      border: `1px solid ${bvColor.border}`,
      borderRadius: `${bvRadius.control}px`,
      color: bvColor.muted,
      transition: bvTransition,
      '&:focus-within': { borderColor: bvColor.accent },
    }}
  >
    <IconSearch />
    <Box
      component="input"
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      sx={{
        flex: 1,
        minWidth: 0,
        border: 'none',
        outline: 'none',
        bgcolor: 'transparent',
        color: bvColor.ink,
        ...bvType.tableSecondary,
        '&::placeholder': { color: bvColor.muted },
      }}
    />
  </Box>
);
